/* ===================================================================
   Supabase browser client (Hopper, 2026-06-09).

   Steve confirmed the auth approach is Supabase Auth (not a custom
   Python login). This is the single browser-side Supabase client the
   whole app shares.

   SECURITY -- load-bearing:
   - This client uses the PUBLISHABLE / ANON key only
     (VITE_SUPABASE_ANON_KEY = sb_publishable_...). That key is public
     and browser-safe: it is RLS-bound. An unauthenticated query against
     public.clients returns 0 rows (verified 2026-06-09).
   - The SERVICE-ROLE key (sb_secret_...) MUST NEVER reach the browser.
     It bypasses RLS. It lives only in engine/.env.local on the server.
   - Do NOT import this module from any server/Node context.

   Env (webapp/.env.local, gitignored):
     VITE_SUPABASE_URL       = https://<ref>.supabase.co
     VITE_SUPABASE_ANON_KEY  = sb_publishable_... (publishable/anon)
   =================================================================== */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/* ===================================================================
   EARLY recovery-flag capture (2026-06-18, robustness pass).

   A Supabase password-recovery link lands on the app with the recovery
   token in the URL fragment (#access_token=...&type=recovery). When this
   module is imported, createClient() below runs with detectSessionInUrl:
   true, which parses AND STRIPS that fragment asynchronously, after which
   `type=recovery` is gone from window.location.hash.

   This block runs at MODULE LOAD -- before createClient() can consume the
   hash, and before React mounts -- and stashes a durable recovery flag in
   sessionStorage. SessionProvider seeds its passwordRecovery state from
   this flag, so the set-password gate shows even if:
     - the async hash strip wins the race, or
     - a stale/existing session is already present (recovery must override
       a normal session, not be shortcut past it by RequireAuth), or
     - the SessionProvider re-mounts.
   The flag is cleared only when the password is actually set (see
   clearRecoveryFlag, called from SetPassword via clearPasswordRecovery).
   =================================================================== */
const RECOVERY_FLAG_KEY = 'rhody_reviews_pw_recovery';

function captureRecoveryFromUrl(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  // Supabase normally uses the fragment; PKCE/error variants may use the query.
  if (/[#&?]type=recovery(\b|&|$)/.test(hash) || /[?&]type=recovery(\b|&|$)/.test(search)) {
    try {
      window.sessionStorage.setItem(RECOVERY_FLAG_KEY, '1');
    } catch {
      /* sessionStorage unavailable (private mode quirk) -- the synchronous
         hash read in SessionProvider is the fallback path. */
    }
  }
}

/** True if a password-recovery flow was detected at load (URL or stashed flag). */
export function isRecoveryFlagged(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem(RECOVERY_FLAG_KEY) === '1') return true;
  } catch {
    /* fall through to the live URL check */
  }
  const hash = window.location.hash || '';
  return /[#&?]type=recovery(\b|&|$)/.test(hash);
}

/** Clear the recovery flag once the new password has been durably set. */
export function clearRecoveryFlag(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(RECOVERY_FLAG_KEY);
  } catch {
    /* no-op */
  }
}

// Capture BEFORE createClient() runs (detectSessionInUrl strips the hash).
captureRecoveryFromUrl();

const SUPABASE_URL: string = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY: string = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

/* Fail loud in dev if the env is missing, but never crash the bundle:
   a missing anon key is an operator action (Steve pastes it), not a bug
   in this code. The Auth screen surfaces a friendly note in that case. */
export const supabaseConfigured: boolean =
  SUPABASE_URL.length > 0 && SUPABASE_ANON_KEY.length > 0;

if (!supabaseConfigured && import.meta.env.DEV) {
  console.warn(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing. ' +
      'Auth is wired but inert until they are set in webapp/.env.local.',
  );
}

/* A single shared client. persistSession + autoRefreshToken keep the
   user signed in across reloads; detectSessionInUrl handles the OAuth
   and password-recovery redirect callbacks landing back on the app. */
export const supabase: SupabaseClient = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: 'rhody_reviews_auth',
    },
  },
);

export { SUPABASE_URL };

/* ===================================================================
   Recovery-session establishment (2026-06-18 fix for "Auth session missing").

   The client is configured flowType:'pkce', but the password-recovery
   emails (sent via the GoTrue /recover endpoint) deliver the session as
   IMPLICIT-flow tokens in the URL FRAGMENT
   (#access_token=...&refresh_token=...&type=recovery). A PKCE client's
   detectSessionInUrl looks for ?code= in the QUERY and does NOT consume
   those fragment tokens, so no session is ever established and
   updateUser({ password }) on the set-password screen fails with
   "Auth session missing".

   This helper parses the fragment tokens and calls setSession() to
   establish the recovery session so the set-password step has a session
   to act on. Idempotent: if a session already exists (e.g. a PKCE ?code=
   link that detectSessionInUrl already handled), it no-ops to true. */
export async function establishRecoverySession(): Promise<boolean> {
  if (typeof window === 'undefined' || !supabaseConfigured) return false;
  // Already have a session? Nothing to do.
  const existing = await supabase.auth.getSession();
  if (existing.data.session) return true;
  // Parse implicit-flow tokens from the URL fragment (still present because
  // the PKCE client never consumed them).
  const raw = (window.location.hash || '').replace(/^#/, '');
  const params = new URLSearchParams(raw);
  const access_token = params.get('access_token') ?? '';
  const refresh_token = params.get('refresh_token') ?? '';
  if (!access_token || !refresh_token) return false;
  const { error } = await supabase.auth.setSession({ access_token, refresh_token });
  return !error;
}

/* ===================================================================
   Integration requests -- the onboarding "suggest your software" form.

   Brad's form UI calls submitIntegrationRequest(). It inserts directly
   via the anon Supabase client (RLS allows any AUTHENTICATED user to
   insert). client_id is OMITTED on purpose: the DB column defaults to
   public.current_client_id(), so the row auto-scopes to the signed-in
   client without the front end needing to know its own client_id. An
   authenticated user not yet linked to a clients row may still submit
   (client_id lands null).

   Backend: engine/db/schema_integration_requests.sql (Hopper, 2026-06-09).
   =================================================================== */
export interface IntegrationRequestInput {
  /** Required. The software/tool the client uses or wants integrated. */
  softwareName: string;
  /** Optional. e.g. "Landscaping", "HVAC", "Dental". */
  businessCategory?: string;
  /** Optional free-text note. */
  note?: string;
  /** Optional but recommended -- prefill from session.user.email. */
  submittedByEmail?: string;
}

export interface IntegrationRequestResult {
  ok: boolean;
  error?: string;
}

export async function submitIntegrationRequest(
  input: IntegrationRequestInput,
): Promise<IntegrationRequestResult> {
  if (!supabaseConfigured) {
    return { ok: false, error: 'Supabase is not configured yet.' };
  }
  const software = input.softwareName.trim();
  if (!software) {
    return { ok: false, error: 'Please enter a software name.' };
  }
  // NOTE: client_id intentionally omitted -- the column default
  // (public.current_client_id()) scopes the row server-side.
  const { error } = await supabase.from('integration_requests').insert({
    software_name: software,
    business_category: input.businessCategory?.trim() || null,
    note: input.note?.trim() || null,
    submitted_by_email: input.submittedByEmail?.trim() || null,
  });
  if (error) {
    return { ok: false, error: error.message || 'Could not submit your request.' };
  }
  return { ok: true };
}
